import {MutableRefObject, useMemo} from 'react';
import {useCanViewAttendanceTrackerAssignments} from '../../../../hooks/use-can-view-attendance-tracker-assignments';
import {useCanViewAttendanceTrackerRegistry} from '../../../../hooks/use-can-view-attendance-tracker-registry';
import {useCanViewEntitySettings} from '../../../../hooks/use-can-view-entity-settings';
import {
  Drawer as DrawerMMui,
  Alert,
  Box,
  useTheme,
  Tab,
  Tabs,
  List,
  Link as MuiLink,
} from '@mui/material';
import {useRouteMatch, Link} from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import AlarmIcon from '@mui/icons-material/Alarm';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import BusinessIcon from '@mui/icons-material/Business';
import InsightsIcon from '@mui/icons-material/Insights';
import FeedIcon from '@mui/icons-material/Feed';
import DevicesIcon from '@mui/icons-material/Devices';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import AppsIcon from '@mui/icons-material/Apps';
import ApiIcon from '@mui/icons-material/Api';
import BadgeIcon from '@mui/icons-material/Badge';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import PlaylistIcon from '@mui/icons-material/PlaylistPlay';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import {useAppContext} from '../../../../contexts/app/context';
import {useOffice} from '../../../../contexts/office/context';
import {appSizes} from '../../../../utils/constants';
import {ListItemLink} from '../../../../components/list-item-link/list-item-link';
import {ProductLogo} from '../../../../components/product-logo/product-logo';
import {CaregiverMode, Environment, ScreenPaths} from '../../../../enums';
import {TabsSystem} from './office.screen';
import {translations} from '../../../../i18n/translations';
import {useAuth} from '../../../../contexts/auth/context';
import {Trans, useTranslation} from 'react-i18next';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import {VideoSettings, WarningRounded} from '@mui/icons-material';
import {OccurrencesCount} from '../../../../components/occurrences/occurrences-count';
import {getBrandName} from '../../../../utils/functions';
import {encodeBase64UrlData} from '../../../../utils/functions/encode-2-base64-url-data';
import {ReactComponent as UsersIcon} from '../../../../images/users.svg';
import {ReactComponent as CaregiversIcon} from '../../../../images/caregivers.svg';

type DrawerProps = {
  tabsSystem: TabsSystem;
  runningMobile: boolean;
  drawerTabValue: number;
  headerHeight: number;
  drawerRef: MutableRefObject<HTMLDivElement | null>;
  isDrawerScrollVisible: boolean;
  onDrawerTabValueChange: (value: number) => void;
};

export const OfficeDrawer = ({
  tabsSystem,
  runningMobile,
  drawerTabValue,
  drawerRef,
  headerHeight,
  isDrawerScrollVisible,
  onDrawerTabValueChange,
}: DrawerProps) => {
  const appContext = useAppContext();
  const canAccessDevelopmentFeatures =
    appContext.state.configuration.environment === Environment.Development;
  const officeContext = useOffice();
  const theme = useTheme();
  const {t} = useTranslation();
  const {state: authState, hasReadPermission} = useAuth();
  const canViewEntitySettings = useCanViewEntitySettings();
  let {path} = useRouteMatch();
  const showProfesionalCaregiverOverviewOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Overview_Activity) ||
      hasReadPermission(RolePermissionType.Overview_Statistics),
    [hasReadPermission]
  );

  const canViewAttendanceTrackerAssignments = useCanViewAttendanceTrackerAssignments();
  const canViewAttendanceTrackerRegistry = useCanViewAttendanceTrackerRegistry();

  const showProfessionalCaregiverTeamOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Team_Professionals) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Profile) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Applications) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Cards) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Playlists) ||
      hasReadPermission(RolePermissionType.Team_Shifts_Management) ||
      hasReadPermission(RolePermissionType.Team_Shifts_Attributions) ||
      hasReadPermission(RolePermissionType.Team_Permissions) ||
      canViewAttendanceTrackerAssignments ||
      canViewAttendanceTrackerRegistry,
    [hasReadPermission, canViewAttendanceTrackerAssignments, canViewAttendanceTrackerRegistry]
  );

  const showProfessionalCaregiverSeniorsOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Seniors_List) ||
      hasReadPermission(RolePermissionType.Seniors_Dashboard) ||
      hasReadPermission(RolePermissionType.Seniors_BADL) ||
      hasReadPermission(RolePermissionType.Seniors_IADL) ||
      hasReadPermission(RolePermissionType.Seniors_ClinicalSupport) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalDevelopment) ||
      (canAccessDevelopmentFeatures &&
        hasReadPermission(RolePermissionType.Seniors_PersonalData_Reminders)) ||
      hasReadPermission(RolePermissionType.Seniors_CareAndActivities) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Profile) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Caregivers) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Health) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Applications) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Devices) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Playlists) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Cards) ||
      hasReadPermission(RolePermissionType.Seniors_Dashboard_Activity),
    [hasReadPermission, canAccessDevelopmentFeatures]
  );

  const showProfessionalCaregiverDigitalInclusionOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.DigitalInclusion_Applications) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Media) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Cards) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Devices) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Services) ||
      hasReadPermission(RolePermissionType.DigitalInclusion_Playlists),
    [hasReadPermission]
  );

  const showProfessionalCaregiverSettingsOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Settings_Entity_Profile) ||
      canViewEntitySettings ||
      hasReadPermission(RolePermissionType.Settings_Licenses) ||
      hasReadPermission(RolePermissionType.Settings_BADL) ||
      hasReadPermission(RolePermissionType.Settings_IADL) ||
      hasReadPermission(RolePermissionType.Settings_ClinicalSupport) ||
      hasReadPermission(RolePermissionType.Settings_PersonalDevelopment) ||
      hasReadPermission(RolePermissionType.Settings_Templates) ||
      hasReadPermission(RolePermissionType.Settings_Occurrences) ||
      hasReadPermission(RolePermissionType.SettingsQuickAccessDevices),
    [hasReadPermission, canViewEntitySettings]
  );

  const showCalendarOptions = useMemo(() => {
    return hasReadPermission(RolePermissionType.Calendar);
  }, [hasReadPermission]);

  const sioslifeAcademyLink = useMemo(() => {
    const data = {
      user: {
        id: authState.session?.currentUser.id,
        name: authState.session?.currentUser.displayName || authState.session?.currentUser.name,
        email: authState.session?.currentUser.email,
        picture: authState.session?.currentUser.picture,
        languageCode: authState.session?.currentUser.languageCode,
        type: authState.session?.currentUser.type,
      },
      entity: {
        id: authState.session?.selectedAffiliation?.entity.id,
        name:
          authState.session?.selectedAffiliation?.entity.shortName ||
          authState.session?.selectedAffiliation?.entity.name,
        picture: authState.session?.selectedAffiliation?.entity.picture,
      },
    };

    return {
      pathname: 'https://academia-sioslife-977281321298.us-west1.run.app/',
      search: `?data=${encodeBase64UrlData(data)}`,
    };
  }, [authState.session?.currentUser, authState.session?.selectedAffiliation?.entity]);

  const DrawerContent = () => {
    const drawerCollapsed =
      officeContext.state.drawerCollapsed &&
      !officeContext.state.drawerCollapeDisabled &&
      !runningMobile;

    return (
      <>
        <Box
          className="desktop-drawer-top-padding"
          sx={{
            height: `${headerHeight}px`,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '16px',
            backgroundColor: 'transparent',
          }}
        >
          {runningMobile && (
            <ProductLogo height={appContext.state.currentThemeSettings.homeLogoHeight} />
          )}
        </Box>
        <Box
          className="menu-items-list"
          sx={{
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
          }}
        >
          {tabsSystem.tabsNeeded > 1 && (
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                marginLeft: '16px',
                marginRight: '16px',
              }}
            >
              {(runningMobile || !officeContext.state.drawerCollapsed) && (
                <Tabs
                  value={drawerTabValue}
                  onChange={(_, newValue) => onDrawerTabValueChange(newValue)}
                  className={
                    tabsSystem.tabsNeeded > 2 ? 'office-screen-caregiver-tabs-big-mode' : ''
                  }
                  sx={{
                    width: appContext.state.currentThemeSettings.drawerWidth - 16 - 16,
                    paddingTop: '4px',
                  }}
                >
                  {tabsSystem.systemAdministratorCaregiverTabEnabled && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px'}}
                      icon={<AdminPanelSettingsIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.system_administrator as string)
                          : ''
                      }
                      component={Link}
                      to={`${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}`}
                    />
                  )}
                  {tabsSystem.partnerCaregiverTabEnabled && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px'}}
                      icon={<SupervisedUserCircleIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.partner as string)
                          : ''
                      }
                      component={Link}
                      to={`${ScreenPaths.Office}${ScreenPaths.Office_Partner}`}
                    />
                  )}
                  {tabsSystem.professionalCaregiverTabEnabled && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px'}}
                      icon={<BusinessIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(
                              translations.office.profile_type_tabs.professional_caregiver as string
                            )
                          : ''
                      }
                      component={Link}
                      to={`${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Dashboard}`}
                    />
                  )}
                  {tabsSystem.informalCaregiverTabEnabled && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px'}}
                      icon={<VolunteerActivismIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.informal_caregiver as string)
                          : ''
                      }
                      component={Link}
                      to={`${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}`}
                    />
                  )}
                </Tabs>
              )}
              {!runningMobile && officeContext.state.drawerCollapsed && (
                <Tabs
                  value={0}
                  className={
                    tabsSystem.tabsNeeded > 2 ? 'office-screen-caregiver-tabs-big-mode' : ''
                  }
                  sx={{
                    width: `${appSizes.sideHamburguerMenu.width.closed - 32}px`,
                    paddingTop: '4px',
                  }}
                >
                  {officeContext.state.currentCaregiverModeEnabled ===
                    CaregiverMode.SystemAdministrator && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px', minWidth: 0}}
                      icon={<AdminPanelSettingsIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.system_administrator as string)
                          : ''
                      }
                      onClick={() => officeContext.collapseDrawer(false, true)}
                    />
                  )}
                  {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Partner && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px', minWidth: 0}}
                      icon={<SupervisedUserCircleIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.partner as string)
                          : ''
                      }
                      onClick={() => officeContext.collapseDrawer(false, true)}
                    />
                  )}
                  {officeContext.state.currentCaregiverModeEnabled ===
                    CaregiverMode.Professional && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px', minWidth: 0}}
                      icon={<BusinessIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(
                              translations.office.profile_type_tabs.professional_caregiver as string
                            )
                          : ''
                      }
                      onClick={() => officeContext.collapseDrawer(false, true)}
                    />
                  )}
                  {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Informal && (
                    <Tab
                      sx={{flex: 1, fontSize: '12px', minWidth: 0}}
                      icon={<VolunteerActivismIcon />}
                      label={
                        tabsSystem.tabsNeeded !== 4
                          ? t(translations.office.profile_type_tabs.informal_caregiver as string)
                          : ''
                      }
                      onClick={() => officeContext.collapseDrawer(false, true)}
                    />
                  )}
                </Tabs>
              )}
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              paddingLeft: '16px',
              paddingRight: !isDrawerScrollVisible ? '16px' : '10px',
              paddingTop: '8px',
            }}
            ref={drawerRef}
            className={`drawer-content ${drawerCollapsed ? 'drawer-content-collapsed' : ''}`}
          >
            {officeContext.state.currentCaregiverModeEnabled ===
              CaregiverMode.SystemAdministrator && (
              <List>
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Activity}`}
                  primary={t(translations.office.system_administrator.tabs.activity) as string}
                  icon={<FeedIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Applications}`}
                  primary={t(translations.office.system_administrator.tabs.applications) as string}
                  icon={<AppsIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Entities}`}
                  primary={t(translations.office.system_administrator.tabs.entities) as string}
                  icon={<BusinessIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Seniors}`}
                  primary={t(translations.office.system_administrator.tabs.seniors as string)}
                  icon={<UsersIcon className="list-item-link-icon" />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Caregivers}`}
                  primary={t(translations.office.system_administrator.tabs.caregivers as string)}
                  icon={<PeopleIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_UserCards}`}
                  primary={t(translations.office.system_administrator.tabs.cards) as string}
                  icon={<BadgeIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Devices}`}
                  primary={t(translations.office.system_administrator.tabs.devices) as string}
                  icon={<DevicesIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Playlists}`}
                  primary={t(translations.office.system_administrator.tabs.playlists) as string}
                  icon={<PlaylistIcon />}
                />
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_GdprErasureRequests}`}
                  primary={
                    t(translations.office.system_administrator.tabs.gdprErasureRequests) as string
                  }
                  icon={<PrivacyTipIcon />}
                />
                {authState.session?.systemAdministratorProfile?.analyticsAdmin === true && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Statistics}`}
                    primary={t(translations.office.system_administrator.tabs.statistics) as string}
                    icon={<InsightsIcon />}
                  />
                )}
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_ApiDocs}`}
                  primary={t(translations.office.system_administrator.tabs.apiDocs) as string}
                  icon={<ApiIcon />}
                />
              </List>
            )}
            {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Partner && (
              <List>
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_Partner}${ScreenPaths.Partner_Home}`}
                  primary={t(translations.office.partner.tabs.home) as string}
                  icon={<HomeIcon />}
                />
                {authState.session?.partnerProfile?.developer === true && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_Partner}${ScreenPaths.Partner_ApiDocs}`}
                    primary={t(translations.office.partner.tabs.apiDocs) as string}
                    icon={<ApiIcon />}
                  />
                )}
              </List>
            )}
            {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Professional && (
              <List>
                {showProfesionalCaregiverOverviewOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Dashboard}`}
                    primary={t(translations.dashboard_side_menu.dashboard) as string}
                    icon={<AppsIcon />}
                  />
                )}
                {showProfessionalCaregiverTeamOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`}
                    primary={t(
                      translations.office.professional_caregiver.tabs
                        .professional_caregivers as string
                    )}
                    icon={<CaregiversIcon className="list-item-link-icon caregivers-icon" />}
                  />
                )}
                {showProfessionalCaregiverSeniorsOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Seniors}`}
                    primary={t(translations.office.professional_caregiver.tabs.seniors as string)}
                    icon={<UsersIcon className="list-item-link-icon" />}
                  />
                )}
                {showCalendarOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner}`}
                    primary={t(translations.office.professional_caregiver.tabs.planner)}
                    icon={<CalendarMonthIcon />}
                  />
                )}
                {hasReadPermission(RolePermissionType.Occurrences) && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Occurrences}`}
                    primary={t(translations.office.professional_caregiver.tabs.occurrences)}
                    icon={
                      <Box sx={{position: 'relative'}}>
                        <OccurrencesCount />
                        <WarningRounded />
                      </Box>
                    }
                  />
                )}
                {showProfessionalCaregiverDigitalInclusionOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`}
                    primary={t(
                      translations.office.professional_caregiver.tabs.digital_inclusion as string
                    )}
                    icon={<VideoSettings />}
                  />
                )}
                {showProfessionalCaregiverSettingsOptions && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`}
                    primary={t(translations.office.professional_caregiver.tabs.settings) as string}
                    icon={<SettingsIcon />}
                  />
                )}
                <ListItemLink
                  to={sioslifeAcademyLink}
                  showTooltip={drawerCollapsed}
                  primary={t(translations.office.professional_caregiver.tabs.sioslifeAcademy)}
                  icon={<SchoolIcon />}
                  target="_blank"
                />
              </List>
            )}
            {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Informal && (
              <List>
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`}
                  primary={t(translations.office.informal_caregiver.tabs.activity) as string}
                  icon={<FeedIcon />}
                />
                {canAccessDevelopmentFeatures &&
                  authState.session?.selectedRelationship?.withUser.id && (
                    <ListItemLink
                      showTooltip={drawerCollapsed}
                      to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session.selectedRelationship.withUser.id}${ScreenPaths.InformalCaregiver_Profile}`}
                      primary={t(translations.family_profile.title)}
                      icon={<BadgeIcon />}
                    />
                  )}
                {canAccessDevelopmentFeatures && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Reminders}`}
                    primary={t(translations.office.informal_caregiver.tabs.reminders) as string}
                    icon={<AlarmIcon />}
                  />
                )}
                {canAccessDevelopmentFeatures && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Medias}`}
                    primary={t(translations.family_media.title) as string}
                    icon={<CollectionsOutlinedIcon />}
                  />
                )}
                {canAccessDevelopmentFeatures &&
                  authState.session?.selectedRelationship?.withUser.id && (
                    <ListItemLink
                      showTooltip={drawerCollapsed}
                      to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session.selectedRelationship.withUser.id}${ScreenPaths.InformalCaregiver_Services}`}
                      primary={t(translations.family_services.title)}
                      icon={<BusinessIcon />}
                    />
                  )}
                {canAccessDevelopmentFeatures && (
                  <ListItemLink
                    showTooltip={drawerCollapsed}
                    to={`${path}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Applications}`}
                    primary={t(translations.family_applications.title)}
                    icon={<AppsIcon />}
                  />
                )}
              </List>
            )}
            {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Informal &&
              !canAccessDevelopmentFeatures && (
                <Box>
                  <Alert severity="info" className="office-screen-informal-caregiver-alert">
                    <Trans
                      i18nKey={t(
                        translations.office.informal_caregiver.caregiver_app_info_message,
                        {
                          brand: getBrandName(appContext.state.configuration.product),
                        }
                      )}
                      components={[
                        <MuiLink
                          target="_blank"
                          color="inherit"
                          href={appContext.state.configuration.caregiverApp.googlePlayStoreUrl}
                        />,
                        <MuiLink
                          target="_blank"
                          color="inherit"
                          href={appContext.state.configuration.caregiverApp.appleStoreUrl}
                        />,
                      ]}
                    />
                  </Alert>
                </Box>
              )}
            {officeContext.state.currentCaregiverModeEnabled === CaregiverMode._None && (
              <List>
                <ListItemLink
                  showTooltip={drawerCollapsed}
                  to={`${path}${ScreenPaths.Office_Me}${ScreenPaths.Me_Home}`}
                  primary={t(translations.office.none.tabs.home) as string}
                  icon={<HomeIcon />}
                />
              </List>
            )}
          </Box>
        </Box>
      </>
    );
  };

  if (runningMobile) {
    return (
      <DrawerMMui
        onBackdropClick={() => {
          if (runningMobile) {
            // Close tray
            officeContext.toggleDrawer(false);
          }
        }}
        variant="temporary"
        anchor="left"
        open={officeContext.state.drawerOpened}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: appContext.state.currentThemeSettings.drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: appContext.state.currentThemeSettings.drawerWidth,
            boxSizing: 'border-box',
            borderRight: 0,
          },
        }}
      >
        {DrawerContent()}
      </DrawerMMui>
    );
  }
  return (
    <DrawerMMui
      variant="persistent"
      anchor="left"
      open={officeContext.state.drawerOpened}
      sx={{
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: officeContext.state.drawerCollapeDisabled
          ? appSizes.sideHamburguerMenu.width.open
          : appSizes.sideHamburguerMenu.width.closed,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width:
            officeContext.state.drawerCollapsed && !officeContext.state.drawerCollapeDisabled
              ? appSizes.sideHamburguerMenu.width.closed
              : appSizes.sideHamburguerMenu.width.open,
          boxSizing: 'border-box',
          boxShadow: 'none',
          borderRight: 0,
          transition: '200ms',
        },
      }}
    >
      {DrawerContent()}
    </DrawerMMui>
  );
};
